/**
 * The calibre editor's clearable top-level fields, and the one rule that makes
 * clearing them stick.
 *
 * Background. `saveCustomCalibre` runs its update payload through a shallow
 * `stripUndefined` before handing it to `updateDoc`, because Firestore rejects
 * `undefined` field values outright. That is correct for the many callers that
 * only ever ADD to a calibre (imports, the AI toolbar, the bulk enricher): they
 * pass `undefined` for everything they don't manage, and the stored value is
 * left alone.
 *
 * It is wrong for the editor, which is the one caller that owns the whole
 * document. There, a field the curator emptied also arrived as `undefined`, was
 * stripped out of the write, and Firestore — which merges rather than replaces
 * on `updateDoc` — kept the previous value. Clearing the Brand on ETA 2826-2,
 * saving, and reopening put the brand straight back, and the same held for the
 * description, the source URL and the last removed image.
 *
 * So the two states have to stay distinguishable all the way to the write, and
 * this module is where that distinction is spelled out exactly once:
 *
 *   - `undefined` — "I don't manage this field." The key is omitted, the stored
 *     value survives.
 *   - `null`, `''`, or `[]` — "the curator cleared this." The key is written as
 *     an explicit `null`, which erases it.
 *   - anything else — written as given.
 *
 * `null` survives `stripUndefined` by construction, so the patch this builds can
 * be spread into the payload without any further gating. The fields listed in
 * `CALIBRE_CLEARABLE_FIELDS` are the top-level scalars/arrays the editor can
 * empty; the nested `data` map needs nothing here because it is written whole
 * and a key missing from it is already a key erased.
 */

import type { CalibreImage } from '../types';

/**
 * A value the editor can clear. `undefined` means "not managed"; every empty
 * form of `T` means "cleared". Keeping the two apart is the entire point — a
 * type that collapsed them would reintroduce the bug.
 */
export type Clearable<T> = T | null | undefined;

export interface CalibreClearableFields {
  brandId?: Clearable<string>;
  description?: Clearable<string>;
  sourceUrl?: Clearable<string>;
  images?: Clearable<CalibreImage[]>;
}

/**
 * The clearable keys, in payload order. Exported so the write path and its
 * tests read from one list rather than two hand-maintained copies — a field
 * added to the editor but not to the payload fails silently, which is precisely
 * how these four came to differ from each other in the first place.
 */
export const CALIBRE_CLEARABLE_FIELDS = [
  'brandId',
  'description',
  'sourceUrl',
  'images',
] as const;

export type CalibreClearableField = (typeof CALIBRE_CLEARABLE_FIELDS)[number];

/** Empty enough to mean "cleared". Whitespace-only strings count; the editor trims. */
function isCleared(value: unknown): boolean {
  if (value === null) return true;
  if (typeof value === 'string') return value.trim().length === 0;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Build the clearable slice of a calibre update payload.
 *
 * Only keys the caller actually passed (i.e. not `undefined`) appear in the
 * result, and each of those is either the value itself or an explicit `null`.
 */
export function calibreClearableUpdate(
  fields: CalibreClearableFields,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of CALIBRE_CLEARABLE_FIELDS) {
    const value = fields[key];
    if (value === undefined) continue;
    patch[key] = isCleared(value) ? null : value;
  }
  return patch;
}
